/**
 * Company Color Palette
 * Standardized colors for consistent branding across the application
 */

export const COMPANY_COLORS = {
  // Primary palette from company configuration
  DARK_BLUE: 'rgb(10, 94, 178)',      // guid: "01"
  TEAL_CYAN: 'rgb(0, 178, 186)',      // guid: "02"  
  NAVY_BLUE: 'rgb(0, 54, 96)',        // guid: "03"
  LIGHT_BLUE: 'rgb(125, 206, 248)',   // guid: "04"
  MEDIUM_BLUE: 'rgb(74, 146, 231)',   // guid: "05"
  DARK_TEAL: 'rgb(0, 111, 121)',      // guid: "06"
  PURPLE: 'rgb(103, 86, 165)',        // guid: "07"
  LIGHT_GREEN: 'rgb(204, 255, 155)',  // guid: "08"
  LIGHT_PURPLE: 'rgb(153, 155, 255)', // guid: "09"
  MAGENTA_PINK: 'rgb(169, 33, 142)',  // guid: "10"

  // cwan-ux: feedback palette (--cwan-palette-*-main)
  SUCCESS: 'rgb(55, 134, 69)',        // #378645
  INFO: 'rgb(0, 130, 138)',           // #00828a
  WARNING: 'rgb(240, 176, 24)',       // #f0b018
  ERROR: 'rgb(219, 31, 48)',          // #db1f30
  NEUTRAL_GREY: 'rgb(109, 110, 113)', // #6d6e71 — cwan neutral series grey
  WHITE: 'rgb(255, 255, 255)'         // #ffffff — cwan common white
  // end cwan-ux
} as const

/**
 * Color palette array for charts and sequential usage
 */
export const COLOR_PALETTE = [
  COMPANY_COLORS.DARK_BLUE,
  COMPANY_COLORS.TEAL_CYAN,
  COMPANY_COLORS.NAVY_BLUE,
  COMPANY_COLORS.LIGHT_BLUE,
  COMPANY_COLORS.MEDIUM_BLUE,
  COMPANY_COLORS.DARK_TEAL,
  COMPANY_COLORS.PURPLE,
  COMPANY_COLORS.LIGHT_GREEN,
  COMPANY_COLORS.LIGHT_PURPLE,
  COMPANY_COLORS.MAGENTA_PINK
] as const

/**
 * Semantic color mappings for specific use cases
 */
export const SEMANTIC_COLORS = {
  // Positive/Negative indicators
  POSITIVE: COMPANY_COLORS.LIGHT_GREEN,  // Green for positive values
  NEGATIVE: COMPANY_COLORS.MAGENTA_PINK, // Pink/Red for negative values
  
  // Selection and highlighting
  SELECTION_BG: COMPANY_COLORS.LIGHT_BLUE,     // Light blue background
  SELECTION_TEXT: COMPANY_COLORS.DARK_BLUE,    // Dark blue text
  
  // Chart series
  PORTFOLIO_BEFORE: COMPANY_COLORS.DARK_BLUE,  // Dark blue
  PORTFOLIO_AFTER: COMPANY_COLORS.TEAL_CYAN,   // Teal/Cyan
  BENCHMARK: COMPANY_COLORS.LIGHT_GREEN,       // Light green
  
  // Status indicators
  PRIMARY: COMPANY_COLORS.DARK_BLUE,
  SECONDARY: COMPANY_COLORS.MEDIUM_BLUE,
  ACCENT: COMPANY_COLORS.PURPLE
} as const

/**
 * Utility function to convert RGB to hex format if needed
 */
export const rgbToHex = (rgb: string): string => {
  const match = rgb.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/)
  if (!match) return rgb
  
  const [, r, g, b] = match
  return `#${parseInt(r).toString(16).padStart(2, '0')}${parseInt(g).toString(16).padStart(2, '0')}${parseInt(b).toString(16).padStart(2, '0')}`
}

/**
 * Utility function to get color with opacity
 */
export const getColorWithOpacity = (color: string, opacity: number): string => {
  const match = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/)
  if (!match) return color
  
  const [, r, g, b] = match
  return `rgba(${r}, ${g}, ${b}, ${opacity})`
}
